import React, { useEffect } from 'react';

const SocialResponsibility = () => {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-on-scroll');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="min-h-[60vh] flex items-center justify-center bg-[#F5F1ED] relative overflow-hidden">
        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/3 w-[500px] h-[500px] bg-[#EDE8E3] rounded-full"></div>
        <div className="container mx-auto px-6 py-20 relative z-10">
          <p className="text-sm text-[#8B7355] uppercase tracking-widest mb-4 fade-on-scroll opacity-0">ESG COMMITMENT</p>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-serif text-[#2A2A2A] mb-6 fade-on-scroll opacity-0">Social Responsibility</h1>
        </div>
      </section>

      {/* Main ESG Statement */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-serif text-[#2A2A2A] mb-8 md:mb-12 leading-tight fade-on-scroll opacity-0">
            Today, success is more than profit, it is the positive impact we can have on our communities and the frameworks through which decisions are made and capital is allocated. At UK Stage, ESG is built into our DNA.
          </h2>
          <div className="space-y-6 text-lg text-[#3D3D3D] leading-relaxed">
            <p className="fade-on-scroll opacity-0">
              We prioritize tangible asset backing, capital protection through structured entry, execution-led value enhancement, and long-term compounding over short-term speculation.
            </p>
            <p className="fade-on-scroll opacity-0">
              For investors and partners seeking asset-backed growth aligned with disciplined execution, UK Stage represents both stability and forward momentum.
            </p>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-0">
        <img 
          src="https://images.pexels.com/photos/3079978/pexels-photo-3079978.jpeg?w=1600&q=80" 
          alt="Indian Education Social Initiative"
          className="w-full h-[220px] sm:h-[400px] md:h-[600px] object-cover fade-on-scroll opacity-0"
        />
      </section>

      {/* ESG Pillars */}
      <section className="py-20 bg-[#EDE8E3]">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-serif text-[#2A2A2A] mb-12 md:mb-16 fade-on-scroll opacity-0">Our ESG Framework</h2>
          
          <div className="grid md:grid-cols-3 gap-12">
            <div className="fade-on-scroll opacity-0">
              <h3 className="text-3xl font-serif text-[#8B7355] mb-6">Environmental</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                We integrate sustainable development practices into all our projects, from land acquisition to construction, ensuring minimal environmental impact and maximum resource efficiency.
              </p>
            </div>
            
            <div className="fade-on-scroll opacity-0">
              <h3 className="text-3xl font-serif text-[#8B7355] mb-6">Social</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                Our commitment extends beyond business to education and community development. Through initiatives like Lakshman Bharti Vidyalaya, we provide accessible education to underprivileged communities.
              </p>
            </div>
            
            <div className="fade-on-scroll opacity-0">
              <h3 className="text-3xl font-serif text-[#8B7355] mb-6">Governance</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                Trust-driven execution and structured dispute resolution define our governance framework. We maintain transparency and accountability in all stakeholder relationships.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Education Initiative */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="fade-on-scroll opacity-0">
              <h2 className="text-4xl font-serif text-[#2A2A2A] mb-6">Lakshman Bharti Vidyalaya</h2>
              <p className="text-lg text-[#3D3D3D] leading-relaxed mb-6">
                An institution originally established as a government-operated school serving economically marginalized communities, now privately managed under a trust structure.
              </p>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                The school focuses on providing accessible, structured education to students from underprivileged backgrounds, ensuring continuity, quality, and administrative stability.
              </p>
            </div>
            <div className="fade-on-scroll opacity-0">
              <img 
                src="https://images.unsplash.com/flagged/photo-1574097656146-0b43b7660cb6?w=800&q=80" 
                alt="Indian Education Initiative"
                className="w-full h-[280px] md:h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Long-term Vision */}
      <section className="py-16 md:py-20 bg-[#2A2A2A] text-white">
        <div className="container mx-auto px-6 max-w-5xl text-center">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-serif mb-8 fade-on-scroll opacity-0">
            Building for Generations
          </h2>
          <p className="text-xl leading-relaxed fade-on-scroll opacity-0">
            Our commitment to ESG principles is not a corporate initiative—it's a fundamental belief that value creation extends beyond balance sheets to the communities we serve and the legacy we leave behind.
          </p>
        </div>
      </section>
    </div>
  );
};

export default SocialResponsibility;